import { createFeatureSelector, createSelector } from '@ngrx/store';
import { UiSelectOption } from '../../shared/components/ui/ui-select/ui-select.models';
import {
  toBaseDetail,
  toBaseRow,
} from '../../features/base-management/utils/base-management.mapper';
import { toBaseCrewMember } from '../../features/base-management/utils/base-crew.mapper';
import { BaseManagementState } from './base-management.reducer';

export const selectBaseManagementState =
  createFeatureSelector<BaseManagementState>('baseManagement');

export const selectBaseAccess = createSelector(selectBaseManagementState, (state) => state.access);

export const selectBaseCanWrite = createSelector(
  selectBaseAccess,
  (access) => access?.canWrite ?? false
);

export const selectBaseRows = createSelector(selectBaseManagementState, (state) =>
  state.bases.map(toBaseRow)
);

export const selectBasePageMeta = createSelector(selectBaseManagementState, (state) => ({
  totalElements: state.totalElements,
  totalPages: state.totalPages,
}));

export const selectBasesLoading = createSelector(
  selectBaseManagementState,
  (state) => state.loading
);

export const selectBasesError = createSelector(
  selectBaseManagementState,
  (state) => state.error
);

const selectSelectedBase = createSelector(selectBaseManagementState, (state) => state.selected);

/** Keyed on `selected` alone, so unrelated state changes (e.g. `saving`) keep the same object. */
export const selectBaseDetail = createSelector(selectSelectedBase, (selected) =>
  selected ? toBaseDetail(selected) : null
);

export const selectBaseDetailError = createSelector(
  selectBaseManagementState,
  (state) => state.detailError
);

export const selectBaseDetailLoading = createSelector(
  selectBaseManagementState,
  (state) => state.detailLoading
);

export const selectBaseSaving = createSelector(selectBaseManagementState, (state) => state.saving);

export const selectBaseWrite = createSelector(selectBaseManagementState, (state) => ({
  revision: state.writeRevision,
  kind: state.lastWrite,
}));

export const selectBaseLeadOptions = createSelector(
  selectBaseManagementState,
  (state): UiSelectOption[] =>
    state.leads
      .filter((lead) => lead.active)
      .map((lead) => ({ value: String(lead.userId), label: lead.fullName }))
);

export const selectBaseTimeZoneOptions = createSelector(
  selectBaseManagementState,
  (state): UiSelectOption[] =>
    state.timeZones.map((timeZone) => ({ value: timeZone, label: timeZone }))
);

export const selectUsedBaseColors = createSelector(
  selectBaseManagementState,
  (state) => state.usedColors
);

export const selectReservedBaseColors = createSelector(
  selectBaseManagementState,
  (state) => state.reservedColors
);

export const selectBaseCrew = createSelector(selectBaseManagementState, (state) =>
  state.crew.map(toBaseCrewMember)
);

export const selectBaseCrewLoading = createSelector(
  selectBaseManagementState,
  (state) => state.crewLoading
);

export const selectBaseCrewTotal = createSelector(
  selectBaseManagementState,
  (state) => state.crewTotal
);

export const selectBaseCrewTotalPages = createSelector(
  selectBaseManagementState,
  (state) => state.crewTotalPages
);

/** userId of the selected base's current lead, or null when none/not loaded. */
export const selectBaseLeadUserId = createSelector(
  selectBaseManagementState,
  (state) => state.selected?.baseLead?.userId ?? null
);

export const selectBaseCrewForbidden = createSelector(
  selectBaseManagementState,
  (state) => state.crewForbidden
);

export const selectLastLeadChange = createSelector(
  selectBaseManagementState,
  (state) => state.lastLeadChange
);
